import { useCallback, useEffect, useMemo, useState } from "react";
import { connect, useDispatch } from "react-redux";
import Modal from "../../../../../components/Modal";
import { Title, Text, SmallText, SuccessIcon, Continue, Cancel, Submit, Actions, Flex } from "../DeleteSessionModal/styles";
import { SessionInDto } from "../../../../../types";
import {
  deleteBulkSession,
  deleteSession,
  resetDeleteBulkSession,
  resetDeleteOneSession,
} from "../../../../../store/actions/session";
import { hideModal } from "../../../../../store/actions/modal";
import { Icon } from "../../../../../components/Icon/styles";
import checkIcon from "../../../../../assets/icons/check-bold.svg";
import trashIcon from "../../../../../assets/icons/trash.svg";
import { useHistory } from "react-router";
import * as Yup from "yup";
import { useTheme } from "styled-components";
import { Formik } from "formik";
import Checkbox from "../../../../../components/FormikForm/Checkbox";
import RichTextEditor from "../../../../../components/FormikForm/RichTextEditor";
import { isFutureDate } from "../../../../../helpers/dates-hanlder";
import { validateCharacterLimitWithContext } from "../../../../../helpers/formBodyCharacterLimitValidation";
import Snackbar from "../../../../../components/Snackbar";

export const schema = Yup.object().shape({
  notify_action: Yup.array().of(Yup.string()).nullable(),
  action_notification_message: Yup.string().nullable()
    .test(
      'character-limit',
      'No puedes usar más de caracteres',
      function (value) {
        return validateCharacterLimitWithContext.call(this, value, 500);
      }
    ),
});

interface FormValues {
  notify_action: string[];
  action_notification_message: string
}

export const initialValues: FormValues = {
  notify_action: ["false"],
  action_notification_message: ""
};

interface ComponentProps {
  selectedSessions: number[];
  sessions?: SessionInDto[];
  deleteBulkSessionStates: {
    loading: boolean;
    success: boolean;
    error: string | boolean;
  };
}

const Component = ({ selectedSessions, sessions, deleteBulkSessionStates }: ComponentProps) => {
  const dispatch = useDispatch();
  const [countdown, setCountdown] = useState(5);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const deletedCount = useMemo(() => sessions?.filter(session => selectedSessions.includes(session.id))?.length || 0, [sessions, selectedSessions]);

  // Filtrar sesiones seleccionadas
  const sessionsToDelete = sessions?.filter(session => selectedSessions.includes(session.id)) || [];

  // Verificar si alguna de las sesiones es futura
  const hasFutureSessions = useMemo(() => sessionsToDelete.some(session => isFutureDate(session?.session_datetime)), [sessionsToDelete]);

  const onSubmit = ({ values, actions }) => {
    dispatch(deleteBulkSession({
      idSessions: selectedSessions,
      ...values,
      notify_action: values.notify_action.includes("true")
    }));
  }

  const onModalClose = useCallback(() => {
    dispatch(hideModal());
    dispatch(resetDeleteBulkSession());
  }, []);

  useEffect(() => {
    if (deleteBulkSessionStates.success) {
      if (countdown > 0) {
        const timer = setTimeout(() => {
          setCountdown(countdown - 1);
        }, 1000);
        return () => clearTimeout(timer);
      } else {
        dispatch(hideModal());
        dispatch(resetDeleteBulkSession());
      }
    }
  }, [countdown, deleteBulkSessionStates.success]);


  return (
    <>
      <Modal
        name="modal-bulk-delete-sessions"
        width={deleteBulkSessionStates.success ? "320" : "500"}
        onClose={() => onModalClose()}
      >
        {deleteBulkSessionStates.success ? (
          <>
            <Flex>
              <SuccessIcon>
                <Icon icon={checkIcon} size="55px" color="#4CA154" />
              </SuccessIcon>
            </Flex>
            <div style={{ height: 28 }} />
            <Title>
              {deletedCount > 1 ? `Los encuentros se eliminaron correctamente` : `El encuentro se eliminó correctamente`}
            </Title>
            <div style={{ height: 8 }} />
            <Text>
              Se eliminaron {deletedCount} encuentro{deletedCount !== 1 ? 's' : ''}.
              Tené en cuenta que esta acción es definitiva y no podrás volver a acceder a ellos.
            </Text>
            <div style={{ height: 28 }} />
            <Actions style={{ justifyContent: 'center' }}>
              <Continue
                onClick={() => {
                  dispatch(hideModal());
                  dispatch(resetDeleteOneSession());
                }}
                href={`/dashboard/${sessionsToDelete[0]?.group?.group_type}/${sessionsToDelete[0]?.group?.id}/sessions/`}
                options={{
                  type: 'filled',
                  size: 'md',
                  skin: 'purple'
                }}
              >
                Volver a Encuentros
              </Continue>
            </Actions>
            <div style={{ height: 8 }} />
            <SmallText>Toca "Volver a encuentros" o aguarda ({countdown} seg.)<br />y seras redirigido </SmallText>
          </>
        ) : (
          <>
            <Title>Vas a eliminar {selectedSessions.length} encuentro{selectedSessions.length !== 1 ? 's' : ''}</Title>
            <div style={{ height: 8 }} />
            <Text>
              Estás por eliminar {selectedSessions.length} encuentro{selectedSessions.length !== 1 ? 's' : ''}.
              Una vez eliminado{selectedSessions.length !== 1 ? 's' : ''} no podrás recuperarlo{selectedSessions.length !== 1 ? 's' : ''}
              y quienes tenían acceso al mismo ya no podrán verlo{selectedSessions.length !== 1 ? 's' : ''}.
            </Text>

            {sessionsToDelete.length > 0 && (
              <>
                <div style={{ height: 16 }} />
                <Text style={{ fontWeight: 'bold' }}>Encuentros a eliminar:</Text>
                <div style={{ height: 8 }} />
                <ul style={{ paddingLeft: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {sessionsToDelete.map(session => (
                    <li key={session.id} style={{ marginBottom: 0, padding: 0, listStyle: 'none' }}>
                      <Text style={{ fontSize: '14px' }}>"{session.title}"</Text>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <div style={{ height: 28 }} />
            <Formik
              initialValues={initialValues}
              onSubmit={(values, actions) => {
                if (onSubmit) onSubmit({ values, actions });
              }}
              validateOnChange={false}
              validateOnBlur={formSubmitted}
              validationSchema={schema}
              enableReinitialize
            >
              {({ handleSubmit, errors, touched, values, setFieldValue, setFieldError, setFieldTouched, handleChange, handleBlur, }) => {
                console.log(errors, values);
                return (
                  <form
                    className="theme-form"
                    onSubmit={(event) => {
                      setFormSubmitted(true);
                      handleSubmit(event);
                    }}
                    id="bulk-delete-sessions-form"
                  >
                    {hasFutureSessions && (
                      <>
                        <Checkbox
                          name="notify_action"
                          error={errors["notify_action"]}
                          touched={touched["notify_action"]}
                          value={values["notify_action"]}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          items={[
                            { label: "Enviar una notificación a tus suscriptores.", value: "true" },
                          ]}
                          options={{
                            marginBottom: 0,
                          }}
                        />

                        <div style={{ height: 20 }} />

                        <span>Agrega un mensaje personalizado explicando el motivo de la cancelación (opcional)</span>

                        <div style={{ height: 12 }} />

                        <RichTextEditor
                          name={"action_notification_message"}
                          entityType="session_reminder"
                          error={errors[`action_notification_message`]}
                          touched={touched[`action_notification_message`]}
                          value={values[`action_notification_message`]}
                          placeholder="Ingresa tu mensaje aquí"
                          showToolbar={false}
                          max={500}
                          onBlur={handleBlur}
                          options={{
                            label: "",
                            height: 100,
                            marginBottom: 20,
                          }}
                        />
                      </>
                    )}
                  </form>
                );
              }}
            </Formik>
            <div style={{ height: 28 }} />
            <Actions>
              <Cancel
                options={{
                  type: "outline",
                  skin: "purple",
                  size: "lg",
                }}
                disabled={deleteBulkSessionStates.loading}
                onClick={() => dispatch(hideModal())}
              >
                Cancelar
              </Cancel>

              <Submit
                form="bulk-delete-sessions-form"
                isSubmmiting={deleteBulkSessionStates.loading}
                options={{
                  type: "filled",
                  skin: "purple",
                  size: "lg",
                }}
                style={{ gap: 6, backgroundColor: "#DC3545", borderColor: "#DC3545" }}
              >
                <Icon icon={trashIcon} size="18px" color="#FFFFFF" />
                Eliminar {selectedSessions.length} encuentro{selectedSessions.length !== 1 ? 's' : ''}
              </Submit>
            </Actions>
          </>
        )}
      </Modal>

      <Snackbar
        visible={deleteBulkSessionStates.success || !!deleteBulkSessionStates.error}
        options={{
          time: 2000,
          type: deleteBulkSessionStates.success ? "success" : "error",
        }}
      >
        {deleteBulkSessionStates.success
          ? "Encuentros eliminados."
          : "Error al eliminar los encuentros."}
      </Snackbar>
    </>
  );
};

const state = ({ sessionStore }) => {
  const { data: sessions, states: sessionsStates } = sessionStore.all;
  const { states: deleteBulkSessionStates } = sessionStore.deleteBulk;

  return {
    sessions,
    sessionsStates,
    deleteBulkSessionStates,
  };
};

export default connect(state)(Component);
